
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Eye } from 'lucide-react';

type FeedbackData = {
  id: string;
  type: 'standard' | 'candidate';
  recipient_name: string;
  job_title: string;
  created_at: string;
  certified: boolean;
};

const FeedbacksGivenList = () => {
  const [feedbacks, setFeedbacks] = useState<FeedbackData[]>([]);
  const [loading, setLoading] = useState(true);
  const { user, profile } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();

  useEffect(() => {
    if (user) {
      fetchFeedbacks();
    }
  }, [user, profile]);

  const fetchFeedbacks = async () => {
    if (!user) return;

    try {
      setLoading(true);
      console.log("Fetching feedbacks given by user:", user.id);
      
      let allFeedbacks: FeedbackData[] = [];
      
      // 1. Récupérer les feedbacks standards (pour les entreprises principalement)
      const { data: standardFeedbacks, error: standardError } = await supabase
        .from('feedbacks')
        .select('id, candidate_name, job_title, created_at, certified')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (standardError) {
        console.error('Erreur lors du chargement des feedbacks standards:', standardError);
      } else if (standardFeedbacks && standardFeedbacks.length > 0) {
        allFeedbacks = standardFeedbacks.map(feedback => ({
          id: feedback.id,
          type: 'standard',
          recipient_name: feedback.candidate_name,
          job_title: feedback.job_title,
          created_at: feedback.created_at,
          certified: feedback.certified || false
        }));
        console.log('Feedbacks standards récupérés:', standardFeedbacks.length);
      }
      
      // 2. Pour les candidats, récupérer aussi les candidate_feedbacks
      if (profile?.role === 'candidat') {
        const { data: candidateFeedbacks, error: candidateError } = await supabase
          .from('candidate_feedbacks')
          .select('id, recruiter_user_id, created_at, rating, feedback_text')
          .eq('candidate_user_id', user.id)
          .order('created_at', { ascending: false });
          
        if (candidateError) {
          console.error('Erreur lors du chargement des feedbacks candidats:', candidateError);
        } else if (candidateFeedbacks && candidateFeedbacks.length > 0) {
          console.log('Candidate feedbacks récupérés:', candidateFeedbacks.length);
          
          // Enrichir les données avec les informations du recruteur
          const enrichedCandidateFeedbacks = await Promise.all(
            candidateFeedbacks.map(async (feedback) => {
              let recruiterName = 'Recruteur';
              let jobTitle = 'Poste non spécifié';
              
              // Récupérer le nom du recruteur si disponible
              if (feedback.recruiter_user_id) {
                const { data: recruiterData } = await supabase
                  .from('profiles')
                  .select('full_name, organization')
                  .eq('id', feedback.recruiter_user_id)
                  .maybeSingle();
                  
                if (recruiterData) {
                  recruiterName = recruiterData.full_name || 'Recruteur';
                  // Si organization est disponible, l'ajouter au nom
                  if (recruiterData.organization) {
                    recruiterName += ` (${recruiterData.organization})`;
                  }
                }
              }
              
              // Essayer de récupérer le titre du poste à partir des feedback_requests
              const { data: requestData } = await supabase
                .from('feedback_requests')
                .select('job_title, message')
                .or(`candidate_user_id.eq.${user.id},user_id.eq.${user.id}`)
                .order('created_at', { ascending: false })
                .limit(5);
                
              if (requestData && requestData.length > 0) {
                // Vérifier si un des messages contient l'ID de ce feedback
                const relatedRequest = requestData.find(req => {
                  if (req.message) {
                    try {
                      const msgData = JSON.parse(req.message);
                      return msgData.candidate_feedback_id === feedback.id;
                    } catch (e) {
                      return false;
                    }
                  }
                  return false;
                });
                
                if (relatedRequest) {
                  jobTitle = relatedRequest.job_title;
                }
              }
              
              return {
                id: feedback.id,
                type: 'candidate',
                recipient_name: recruiterName,
                job_title: jobTitle,
                created_at: feedback.created_at,
                certified: false // Les feedbacks candidats ne sont pas certifiés
              } as FeedbackData;
            })
          );
          
          allFeedbacks = [...allFeedbacks, ...enrichedCandidateFeedbacks];
        }
      }
      
      // Trier tous les feedbacks par date de création
      allFeedbacks.sort((a, b) => 
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      );
      
      setFeedbacks(allFeedbacks);
      console.log("Total des feedbacks donnés récupérés:", allFeedbacks.length);
    } catch (error: any) {
      console.error('Erreur lors du chargement des feedbacks donnés:', error);
      toast({
        title: "Erreur",
        description: error.message || "Impossible de charger les feedbacks donnés",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleViewFeedback = (id: string, type: 'standard' | 'candidate') => {
    if (type === 'candidate') {
      navigate(`/view-candidate-feedback/${id}`);
    } else {
      navigate(`/view-feedback/${id}`);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-8">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-brandBlue"></div>
      </div>
    );
  }

  if (feedbacks.length === 0) {
    return (
      <Card className="shadow-sm">
        <CardHeader>
          <CardTitle>Feedbacks donnés</CardTitle>
          <CardDescription>
            {profile?.role === 'candidat'
              ? "Vous n'avez pas encore donné de feedbacks aux recruteurs"
              : "Vous n'avez pas encore donné de feedbacks aux candidats"}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8">
            <p className="text-gray-500">
              {profile?.role === 'candidat'
                ? "Les feedbacks que vous donnerez aux recruteurs apparaîtront ici"
                : "Les feedbacks que vous donnerez aux candidats apparaîtront ici"}
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="shadow-sm">
      <CardHeader>
        <CardTitle>Feedbacks donnés</CardTitle>
        <CardDescription>
          {profile?.role === 'candidat'
            ? "Liste des feedbacks que vous avez donnés aux recruteurs"
            : "Liste des feedbacks que vous avez donnés aux candidats"}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>À</TableHead>
              <TableHead>Poste</TableHead>
              <TableHead>Date</TableHead>
              <TableHead>Statut</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {feedbacks.map((feedback) => (
              <TableRow key={feedback.id}>
                <TableCell>{feedback.recipient_name}</TableCell>
                <TableCell>{feedback.job_title}</TableCell>
                <TableCell>{new Date(feedback.created_at).toLocaleDateString()}</TableCell>
                <TableCell>
                  <Badge variant={feedback.certified ? 'default' : 'outline'}>
                    {feedback.certified ? 'Certifié' : 'Non certifié'}
                  </Badge>
                </TableCell>
                <TableCell className="text-right">
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => handleViewFeedback(feedback.id, feedback.type)}
                  >
                    <Eye size={16} className="mr-1" /> Voir
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
};

export default FeedbacksGivenList;
